<template>
  <div class="ios-time-grid"
       :cy="cyRoot"
       :class="{ 'ios-time-grid--disabled': disabled }">
    <div class="ios-time-grid-column">
      <span class="ios-time-grid-label"
            :cy="`${cyRoot}-hours-label`">{{ hoursLabel }}</span>
      <div class="ios-time-grid-tiles ios-time-grid-tiles--hours"
           :cy="`${cyRoot}-hours`">
        <button v-for="h in hours"
                :key="`h-${h}`"
                type="button"
                class="ios-time-grid-tile"
                :cy="`${cyRoot}-hour-${h}`"
                :class="{ 'ios-time-grid-tile--active': h === selectedHourNum }"
                :disabled="disabled"
                @click="pickHour(h)">
          {{ h }}
        </button>
      </div>
    </div>

    <div class="ios-time-grid-column">
      <span class="ios-time-grid-label"
            :cy="`${cyRoot}-minutes-label`">{{ minutesLabel }}</span>
      <div class="ios-time-grid-tiles ios-time-grid-tiles--minutes"
           :cy="`${cyRoot}-minutes`">
        <button v-for="m in minutes"
                :key="`m-${m}`"
                type="button"
                class="ios-time-grid-tile"
                :cy="`${cyRoot}-minute-${m}`"
                :class="{ 'ios-time-grid-tile--active': m === selectedMinuteNum }"
                :disabled="disabled"
                @click="pickMinute(m)">
          {{ pad(m) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  const TIME_RE = /^(\d{1,2}):(\d{1,2})$/
  const HOUR_MIN = 8
  const HOUR_MAX = 20
  const MINUTE_STEP = 5

  function pad (n) {
    return String(n).padStart(2, '0')
  }

  function buildHours () {
    const out = []
    for (let h = HOUR_MIN; h <= HOUR_MAX; h++) out.push(h)
    return out
  }

  function buildMinutes () {
    const out = []
    for (let m = 0; m < 60; m += MINUTE_STEP) out.push(m)
    return out
  }

  export default {
    name: 'IOSTimeGrid',
    props: {
      value: {
        type: String,
        default: ''
      },
      cy: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      hoursLabel: {
        type: String,
        default: 'Ora:'
      },
      minutesLabel: {
        type: String,
        default: 'Minute'
      }
    },
    data () {
      return {
        selectedHourNum: HOUR_MIN,
        selectedMinuteNum: 0
      }
    },
    computed: {
      cyRoot () {
        return this.cy || 'ios-time-grid'
      },
      hours () {
        return buildHours()
      },
      minutes () {
        return buildMinutes()
      }
    },
    watch: {
      value: {
        immediate: true,
        handler (newVal) {
          this._syncFromValue(newVal)
        }
      }
    },
    mounted () {
      this._emitValue()
    },
    methods: {
      _syncFromValue (v) {
        const m = TIME_RE.exec(v || '')
        if (!m) return
        const h = parseInt(m[1], 10)
        const min = parseInt(m[2], 10)
        if (!Number.isNaN(h)) {
          this.selectedHourNum = Math.min(HOUR_MAX, Math.max(HOUR_MIN, h))
        }
        if (!Number.isNaN(min)) {
          const snapped = Math.round(min / MINUTE_STEP) * MINUTE_STEP
          this.selectedMinuteNum = Math.min(55, Math.max(0, snapped))
        }
      },
      pickHour (h) {
        if (this.disabled) return
        this.selectedHourNum = h
        this._emitValue()
      },
      pickMinute (m) {
        if (this.disabled) return
        this.selectedMinuteNum = m
        this._emitValue()
      },
      _emitValue () {
        this.$emit('input', `${pad(this.selectedHourNum)}:${pad(this.selectedMinuteNum)}`)
      },
      pad (n) {
        return pad(n)
      }
    }
  }
</script>

<style lang="less">
  @import "../../styles/var";

  @ios-tg-bg: #FFFFFF;
  @ios-tg-text: #1C1C1E;
  @ios-tg-muted: rgba(60, 60, 67, 0.55);
  @ios-tg-tile-border: rgba(60, 60, 67, 0.14);
  @ios-tg-accent: #007AFF;
  @ios-tg-accent-fade: rgba(0, 122, 255, 0.12);
  @ios-tg-accent-text: #FFFFFF;
  @ios-tg-tile-size: 34px;
  @ios-tg-gap: 8px;

  .ios-time-grid {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    padding: 12px;
    background: @ios-tg-bg;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.04);
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    color: @ios-tg-text;
    user-select: none;

    &--disabled {
      opacity: 0.55;
      pointer-events: none;
    }

    .ios-time-grid-column {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      min-width: 0;
      flex: 0 0 auto;
    }

    .ios-time-grid-label {
      align-self: flex-start;
      font-size: 1.7rem;
      font-weight: 600;
      letter-spacing: -0.2px;
      color: @ios-tg-text;
      padding: 0 2px;
    }

    .ios-time-grid-tiles {
      display: grid;
      grid-template-columns: repeat(5, 48px);
      grid-auto-rows: 40px;
      gap: 0px;
    }

    .ios-time-grid-tile {
      display: flex;
      align-items: center;
      justify-content: center;
      place-self: center;
      width: @ios-tg-tile-size;
      height: @ios-tg-tile-size;
      padding: 0;
      background: @ios-tg-bg;
      border: 1px solid @ios-tg-tile-border;
      border-radius: 5px;
      font-family: inherit;
      font-size: 1.6rem;
      font-weight: 500;
      color: @ios-tg-text;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.05s ease;

      &:hover:not(:disabled):not(&--active) {
        background: @ios-tg-accent-fade;
      }

      &:active:not(:disabled) {
        transform: scale(0.96);
      }

      &--active {
        background: @ios-tg-accent;
        border-color: @ios-tg-accent;
        color: @ios-tg-accent-text;
        font-weight: 600;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    @media only screen and (max-width: 1023px) {
      .ios-time-grid-tiles {
        grid-template-columns: repeat(5, 60px);
        grid-auto-rows: 60px;
      }

      .ios-time-grid-tile {
        width: 40px;
        height: 40px;
        font-size: 1.7rem;
      }
    }

  }
</style>
